import type {
    CertificateResource,
    ChannelResource,
    ParentEnvironmentResource,
    SigningKeyConfigurationResource,
    SpaceResource,
    PackageResource,
    FeatureToggleEnvironmentResource,
    UpgradeConfigurationResource,
} from "@octopusdeploy/octopus-server-client";
import type { JiraServiceManagementIntegrationConnection } from "~/areas/configuration/components/Settings/JiraServiceManagementIntegrationSettingsPage";
import type { AddFeatureToggleEnvironmentWorkflowInput, EditFeatureToggleEnvironmentWorkflowInput } from "~/areas/projects/components/FeatureToggles/Workflows/FeatureToggleEnvironmentWorkflow";
import type { AddCertificateWorkflowInput } from "~/workflows/AddCertificateWorkflowInput";
import type { AddJiraServiceManagementConnectionWorkflowInput, EditJiraServiceManagementConnectionWorkflowInput } from "~/workflows/JiraServiceManagementConnectionWorkflowInput";
import type { EditChannelWorkflowInput } from "./EditChannelWorkflowInput";
import type { EditParentEnvironmentWorkflowInput } from "./EditParentEnvironmentWorkflowInput";
import type { RotateExternalSigningKeyWorkflowInput } from "./RotateExternalSigningKeyWorkflowInput";
import type { ViewProjectTemplateUsagesWorkflowInput } from "./ViewProjectTemplateUsagesWorkflowInput";

export const workflows = {
    addSpaceWorkflow: createDialogWorkflow<_, SpaceResource>("addSpaceWorkflow"),
    addCertificateWorkflow: createDrawerWorkflow<AddCertificateWorkflowInput, CertificateResource>("addCertificateWorkflow"),
    viewProjectTemplateUsagesWorkflow: createDialogWorkflow<ViewProjectTemplateUsagesWorkflowInput, _>("viewProjectTemplateUsagesWorkflow"),
    addJiraServiceManagementConnectionWorkflow: createDrawerWorkflow<AddJiraServiceManagementConnectionWorkflowInput, JiraServiceManagementIntegrationConnection>("addJiraServiceManagementConnectionWorkflow"),
    editJiraServiceManagementConnectionWorkflow: createDrawerWorkflow<EditJiraServiceManagementConnectionWorkflowInput, JiraServiceManagementIntegrationConnection>("editJiraServiceManagementConnectionWorkflow"),
    editChannelWorkflow: createDrawerWorkflow<EditChannelWorkflowInput, ChannelResource>("editChannelWorkflow"),
    editParentEnvironmentWorkflow: createDrawerWorkflow<EditParentEnvironmentWorkflowInput, ParentEnvironmentResource>("editParentEnvironmentWorkflow"),
    uploadPackageWorkflow: createDialogWorkflow<_, PackageResource>("uploadPackageWorkflow"),
    editSigningKeyConfigurationWorkflow: createDialogWorkflow<_, SigningKeyConfigurationResource>("editSigningKeyConfigurationWorkflow"),
    rotateExternalSigningKeyWorkflow: createDialogWorkflow<RotateExternalSigningKeyWorkflowInput, void>("rotateExternalSigningKeyWorkflow"),
    addFeatureToggleEnvironmentWorkflow: createDrawerWorkflow<AddFeatureToggleEnvironmentWorkflowInput, FeatureToggleEnvironmentResource>("addFeatureToggleEnvironmentWorkflow"),
    editFeatureToggleEnvironmentWorkflow: createDrawerWorkflow<EditFeatureToggleEnvironmentWorkflowInput, FeatureToggleEnvironmentResource>("editFeatureToggleEnvironmentWorkflow"),
    updateNotificationsWorkflow: createDialogWorkflow<_, UpgradeConfigurationResource>("updateNotificationsWorkflow"),
};

export function createDialogWorkflow<Input, Output>(workflowId: string): DialogWorkflow<Input, Output> {
    return {
        workflowId,
        workflowType: "dialog",
    };
}

export function createDrawerWorkflow<Input, Output>(workflowId: string): DrawerWorkflow<Input, Output> {
    return {
        workflowId,
        workflowType: "drawer",
    };
}

export interface DialogWorkflow<Input, Output> {
    workflowId: string;
    workflowType: "dialog";
}

export interface DrawerWorkflow<Input, Output> {
    workflowId: string;
    workflowType: "drawer";
}

export type Workflow<Input, Output> = DialogWorkflow<Input, Output> | DrawerWorkflow<Input, Output>;

export type NoWorkflowParameter = { readonly __noParameter: unique symbol };

// noinspection JSClassNamingConvention
type _ = NoWorkflowParameter;
